import React, { memo } from 'react';
import { Tag } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  InboxOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { WorkOrderLineItem } from '../../types';
import { formatCurrency } from '../../utils/formatCurrency';

interface LineItemRowProps {
  item: WorkOrderLineItem;
  layout: 'card' | 'table-row';
  isMobile: boolean;
  isLocked: boolean;
  onTap: () => void;
}

const shopStatusConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
  new: { color: 'blue', label: 'New', icon: <InboxOutlined /> },
  in_progress: { color: 'processing', label: 'In Progress', icon: <ToolOutlined /> },
  waiting_parts: { color: 'orange', label: 'Waiting Parts', icon: <ClockCircleOutlined /> },
  need_approval: { color: 'warning', label: 'Needs Approval', icon: <WarningOutlined /> },
  completed: { color: 'success', label: 'Completed', icon: <CheckCircleOutlined /> },
};

const approvalColor: Record<string, string> = {
  pending: 'default',
  approved: 'success',
  declined: 'error',
};

const computeLineTotal = (item: WorkOrderLineItem): number => {
  if (item.line_total != null) return item.line_total;
  const laborTotal = ((item.labor_hours || 0) * (item.labor_rate || 0)) - (item.labor_discount_amount || 0);
  const partsTotal = item.parts?.reduce((sum, p) => sum + p.quantity * p.unit_price, 0) || 0;
  return laborTotal + partsTotal;
};

/** Single line item displayed as a compact card (mobile) or inline content (desktop table row). */
const LineItemRowInner: React.FC<LineItemRowProps> = ({ item, layout, isMobile, isLocked, onTap }) => {
  const total = computeLineTotal(item);
  const status = shopStatusConfig[item.shop_status] || shopStatusConfig.new;

  if (layout === 'card') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onTap}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); } }}
        style={{
          background: '#ffffff',
          borderRadius: 8,
          border: '1px solid #e5e7eb',
          padding: isMobile ? 12 : 16,
          minHeight: 48,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          transition: 'box-shadow 0.15s',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: isMobile ? 14 : 15,
              fontWeight: 500,
              color: '#111827',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {item.concern || 'Untitled item'}
            </div>
            {item.correction && (
              <div style={{
                fontSize: isMobile ? 12 : 13,
                color: '#6b7280',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {item.correction}
              </div>
            )}
          </div>
          <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
            {formatCurrency(total)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tag color={status.color} icon={status.icon} style={{ margin: 0, fontSize: 11 }}>
            {status.label}
          </Tag>
          <Tag color={approvalColor[item.customer_approval_status] || 'default'} style={{ margin: 0, fontSize: 11 }}>
            {item.customer_approval_status}
          </Tag>
          {item.labor_hours != null && item.labor_hours > 0 && (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{item.labor_hours}h labor</span>
          )}
          {item.parts && item.parts.length > 0 && (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{item.parts.length} part{item.parts.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    );
  }

  // Desktop table-row layout is handled by the Ant Design Table columns in LineItemList.
  // This component is only used for card rendering.
  return null;
};

export const LineItemRow = memo(LineItemRowInner);
